(() => {
  'use strict';
  const main = document.querySelector('main');
  if (!main || !('IntersectionObserver' in window)) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 760px)');
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  // Keep each existing image, its alt text, and its responsive source. Only add a crop.
  const crops = [
    ['.day-media>img', 'day'],
    ['.face-gallery figure>img', 'face'],
    ['img.requirement-photo', 'requirement', 'requirement-photo'],
    ['img.promise-photo', 'promise', 'promise-photo'],
    ['.article-opening .article-hero>img', 'article-hero'],
    ['.article-body figure>img', 'article-body'],
    ['.contact-photo>img', 'contact']
  ];
  crops.forEach(([selector, kind, layoutClass]) => {
    main.querySelectorAll(selector).forEach(image => {
      const frame = document.createElement('span');
      frame.className = `sv-photo-frame sv-photo-frame--${kind}`;
      if (layoutClass) {
        frame.classList.add(layoutClass);
        image.classList.remove(layoutClass);
      }
      image.before(frame);
      frame.append(image);
    });
  });
  const selectors = [
    '.hero-image>img', '.home-editorial-photo>img', '.ambition-photo>img',
    '.story-founder-image>img', '.story-photo-pair figure>div>img',
    '.day-inset>img', '.journal-photo>img', '.launch-visual>img', '.sv-photo-frame>img'
  ];
  const photos = [...main.querySelectorAll(selectors.join(','))].map((image, index) => {
    const frame = image.parentElement;
    image.classList.add('sv-photo');
    frame.classList.add('sv-photo-clip');
    return { image, frame, strength: .85 + (index % 3) * .075 };
  });
  const hero = main.querySelector('.hero-visual');
  const layers = [...main.querySelectorAll('.hero .sun-disc,.hero .floating-portrait')].map(element => ({
    element, sun: element.classList.contains('sun-disc'), y: 0
  }));
  const protectedCopy = [];
  main.querySelectorAll('.hero-copy>.eyebrow,.hero-copy>h1,.hero-description,.hero-copy>.button,.hero-footnote,.hero-caption').forEach(element => {
    if (element.matches('a,button,.hero-caption')) { protectedCopy.push(element); return; }
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      if (!walker.currentNode.textContent.trim()) continue;
      const range = document.createRange();
      range.selectNodeContents(walker.currentNode);
      protectedCopy.push(range);
    }
  });
  const active = new Set();
  const byFrame = new Map(photos.map(photo => [photo.frame, photo]));
  let heroVisible = false;
  let frameRequest = 0;
  let hasScrolled = window.scrollY > 0;

  // Constrain decorative layers against the real, responsive text rectangles.
  const safeLayerY = (base, desired, copy) => {
    let low = -Infinity;
    let high = Infinity;
    const gap = mobile.matches ? 8 : 12;
    for (const text of copy) {
      if (!text.width || !text.height || base.right <= text.left - gap || base.left >= text.right + gap) continue;
      if (base.bottom <= text.top) high = Math.min(high, Math.max(0, text.top - base.bottom - gap));
      else if (base.top >= text.bottom) low = Math.max(low, Math.min(0, text.bottom - base.top + gap));
      else return 0;
    }
    return low <= high ? clamp(desired, low, high) : 0;
  };
  const update = () => {
    frameRequest = 0;
    if (!hasScrolled || reduced.matches || document.hidden) return;
    const viewport = document.documentElement.clientHeight;
    const writes = [];
    // Batch geometry reads before changing compositor properties.
    for (const photo of active) {
      const rect = photo.frame.getBoundingClientRect();
      const small = rect.width < 120 || rect.height < 100;
      const phase = clamp((viewport / 2 - rect.top - rect.height / 2) / ((viewport + rect.height) / 2), -1, 1);
      const mobileHero = mobile.matches && photo.frame.classList.contains('hero-image');
      const maxShift = mobileHero ? 4 : mobile.matches ? 7 : 8;
      const cropAllowance = mobileHero ? .012 : mobile.matches ? .02 : .014;
      const amplitude = small ? 0 : Math.min(maxShift, photo.frame.clientHeight * cropAllowance) * photo.strength;
      const desiredY = -phase * amplitude;
      const baseScale = mobileHero ? 1.03 : mobile.matches ? 1.045 : 1.04;
      const scaleTravel = mobileHero ? .004 : mobile.matches ? .012 : .008;
      const desiredScale = small ? 1 : baseScale + Math.abs(phase) * scaleTravel;
      const {scale, y} = window.SoluveliPhotoFocus?.motion(photo.image, desiredScale, desiredY) || {scale:desiredScale, y:desiredY};
      writes.push(() => {
        photo.image.style.setProperty('--sv-photo-y', `${y.toFixed(2)}px`);
        photo.image.style.setProperty('--sv-photo-scale', scale.toFixed(4));
        if (photo.frame.classList.contains('launch-visual')) photo.frame.style.setProperty('--sv-sun-turn', `${(phase * 5).toFixed(2)}deg`);
      });
    }
    if (heroVisible && hero) {
      const rect = hero.getBoundingClientRect();
      const end = Math.max(400, rect.bottom + window.scrollY);
      const progress = clamp(window.scrollY / end, 0, 1);
      const copy = protectedCopy.flatMap(target => [...target.getClientRects()]);
      layers.forEach(layer => {
        const rect = layer.element.getBoundingClientRect();
        const base = { left: rect.left, right: rect.right, top: rect.top - layer.y, bottom: rect.bottom - layer.y };
        // A short, opposing drift keeps the mobile hero calm and its layers apart.
        const limit = layer.sun ? (mobile.matches ? -12 : 20) : (mobile.matches ? 8 : -12);
        const y = safeLayerY(base, progress * limit, copy);
        writes.push(() => {
          layer.y = y;
          layer.element.style.setProperty('--sv-layer-y', `${y.toFixed(2)}px`);
        });
      });
    }
    writes.forEach(write => write());
  };
  const queue = () => {
    if (hasScrolled && !frameRequest && !reduced.matches && !document.hidden) frameRequest = requestAnimationFrame(update);
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.target === hero) heroVisible = entry.isIntersecting;
      const photo = byFrame.get(entry.target);
      if (!photo) return;
      if (entry.isIntersecting) active.add(photo);
      else active.delete(photo);
      photo.frame.classList.toggle('sv-photo-active', entry.isIntersecting);
    });
    queue();
  }, { rootMargin: '100px 0px', threshold: 0 });
  photos.forEach(photo => observer.observe(photo.frame));
  if (hero) observer.observe(hero);
  if ('ResizeObserver' in window) {
    const resize = new ResizeObserver(queue);
    photos.forEach(photo => resize.observe(photo.frame));
    if (hero) resize.observe(hero);
  }
  const syncPreference = () => {
    document.body.classList.toggle('sv-motion-enabled', !reduced.matches);
    if (reduced.matches) {
      if (frameRequest) cancelAnimationFrame(frameRequest);
      frameRequest = 0;
      layers.forEach(layer => { layer.y = 0; layer.element.style.removeProperty('--sv-layer-y'); });
    } else queue();
  };
  document.body.classList.add('sv-motion-ready');
  // Preparing compositing layers also costs a paint. Leave the initial hero
  // static until motion is actually needed, including restored scroll positions.
  document.body.classList.toggle('sv-motion-started', hasScrolled);
  reduced.addEventListener('change', syncPreference);
  mobile.addEventListener('change', queue);
  // Capture also covers the horizontal photo rails; it never changes their scrolling.
  document.addEventListener('scroll', () => {
    if (!hasScrolled) {
      hasScrolled = true;
      document.body.classList.add('sv-motion-started');
    }
    queue();
  }, { passive: true, capture: true });
  document.addEventListener('soluveli:photo-focus', queue);
  window.addEventListener('resize', queue, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && frameRequest) { cancelAnimationFrame(frameRequest); frameRequest = 0; }
    else queue();
  });
  document.fonts?.ready.then(queue);
  syncPreference();
})();
